import React, { useState, useMemo } from 'react';
import CardComponent from 'components/common/CardComponent';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import SelectComponentStandards from 'components/common/SelectComponentStandards';
import { committeeCyclesToCommitteeSelectOptions, committeeCyclesToCycleSelectOptions } from 'util/ballot';
import routes from 'store/configs/Routes';
import { useStandardsBallotContext } from '../StandardsBallotContext';
import { CommitteeCycleModel, StandardsBallotModel } from 'store/types/StandardsBallot';

import './AllStandardsBallotsComponent.scss';

const AllStandardsBallotsComponent: React.FunctionComponent = () => {
  const { committeeCycles = [], votingAnnouncements = [] } = useStandardsBallotContext();

  const [selectedCommittee, setSelectedCommittee] = useState('');
  const [selectedCycle, setSelectedCycle] = useState('');

  const selectedCommitteeCycle: CommitteeCycleModel | undefined = useMemo(() => {
    if (selectedCommittee && selectedCycle) {
      return committeeCycles.find((cc) => cc.committee === selectedCommittee && cc.period === selectedCycle);
    }
    return undefined;
  }, [selectedCommittee, selectedCycle, committeeCycles]);

  return (
    <div id="allStandardsBallotsComponent" className="container my-5">
      <div className="row">
        <div className="col-12 col-lg-6">
          <CardComponent title="Standards Ballots">
            <div className="description-text">
              <p>
                Technical ballots are used by the SEMI Standards Program to review the technical content review of
                documents under development. Ballots are considered the major step in the formal approval process prior
                to publishing of a new SEMI Standard or a revision of an existing SEMI Standard.
              </p>
              <p>
                For those new to the SEMI International Standards Program, please refer to the section About Standards,
                as well as the SEMI Standards Regulations, which are the rules by which the program is governed.
              </p>
              <p>Use the pull-down menu below to browse an individual committee's technical ballots.</p>
            </div>

            <SelectComponentStandards
              label="Ballot Period"
              value={selectedCycle}
              selectOptions={committeeCyclesToCycleSelectOptions(committeeCycles)}
              placeholder="Select Ballot Period"
              onChange={(e) => {
                setSelectedCycle(e.target.value);
                setSelectedCommittee('');
              }}
            />

            <SelectComponentStandards
              label="Ballot Committee"
              value={selectedCommittee}
              selectOptions={committeeCyclesToCommitteeSelectOptions(
                committeeCycles.filter((cc) => cc.period === selectedCycle)
              )}
              disabled={!selectedCycle}
              placeholder="Select Committee"
              onChange={(e) => setSelectedCommittee(e.target.value)}
            />
          </CardComponent>
        </div>

        <div className="col-12 col-lg-6" id="voting-card">
          <CardComponent title="Voting Announcements" buttonText="Vote" editRoute={routes.standardsBallot.myBallots}>
            {votingAnnouncements.map((announcement, i) => (
              <CardFieldRowComponent key={i} value={announcement} includeDivider={i < votingAnnouncements.length - 1} />
            ))}
          </CardComponent>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-12">
          <CardComponent title="Standards Ballots">
            {selectedCommitteeCycle ? (
              selectedCommitteeCycle.standardsBallot.map((ballot: StandardsBallotModel, index: number) => {
                const title = [selectedCommitteeCycle.committee, selectedCommitteeCycle.period, 'Ballots'].join(' ');

                return (
                  <div key={index}>
                    <CardFieldRowComponent title={title} subtitle={ballot.subtitle} />
                    <a href={ballot.fileURL} download>
                      {ballot.fileName}
                    </a>
                    {ballot.details && (
                      <div className="description-text">
                        <ul className="bullet-list">
                          {ballot.details.map((detail, i) => (
                            <li key={i}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="card-divider-dashed w-100 mb-3" />
                  </div>
                );
              })
            ) : (
              <>
                <CardFieldRowComponent
                  subtitle={'Please select a committee and a cycle above to view available ballots.'}
                />
                <div className="card-divider-dashed w-100 mb-3" />
              </>
            )}
          </CardComponent>
        </div>
      </div>
    </div>
  );
};

export default AllStandardsBallotsComponent;
